/**
 * Entitlement denials (HTTP 402) — the SPA's vocabulary and its Norwegian copy.
 *
 * The API refuses a request that clears the ROLE axis but fails the
 * ENTITLEMENT axis with `402 Payment Required` and an RFC 9457 problem
 * document whose machine-readable discriminator sits at the TOP LEVEL
 * (extension members), precisely so a client never has to parse Norwegian
 * prose:
 *
 *   { "type": "/problems/entitlement-required", "title": "Premium kreves",
 *     "status": 402, "detail": "<norsk tekst>", "code": "recipe_limit",
 *     "limit": 15, "current": 15 }
 *
 * `limit`/`current` are only present for the numeric limit (`recipe_limit`).
 * Source of truth for the vocabulary: `apps/api/src/auth/
 * entitlement-required.exception.ts` (`EntitlementCode`). This file MIRRORS it
 * for the client; it is a separate copy on purpose — `apps/api` is not a
 * dependency of the SPA — and an unknown value from a NEWER server must never
 * break the UI (see `UNKNOWN_ENTITLEMENT_CODE`).
 *
 * ── Who owns the words ───────────────────────────────────────────────────────
 * THE UI DOES. The server's `detail` is a safe fallback for a non-UI client,
 * not our copy: `entitlementMessage` below is the single place the user-facing
 * Norwegian lives, so the same limit reads the same everywhere (editor, invite
 * form, import buttons) and can be re-worded without an API deploy.
 *
 * 402 is NOT 403. A 403 means "your role is too weak — ask the owner"; a 402
 * means "your role is fine, this capability simply isn't in the plan". They
 * must never collapse into one message (that distinction is the whole reason
 * the API spends two status codes on it).
 */

/**
 * Every limit the API can refuse a request with today. Mirrors the API's
 * `EntitlementCode` union exactly.
 */
export const ENTITLEMENT_CODES = [
  /** A free cookbook holds at most `FREE_PLAN_RECIPE_LIMIT` recipes (numeric). */
  "recipe_limit",
  /** Sharing a cookbook at all — raised by the INVITE path only. */
  "sharing",
  /** AI import from photos. */
  "photo_import",
  /** AI import from a PDF. */
  "pdf_import",
  /** AI import from a video. */
  "video_import",
] as const;

export type EntitlementCode = (typeof ENTITLEMENT_CODES)[number];

/**
 * The code we use when the server sent a 402 we don't recognise — a newer API
 * with a code this build predates, or a body that wasn't the expected shape at
 * all. A 402 always means the same thing at the top level ("this needs
 * Premium"), so an unknown code degrades to that generic-but-correct message
 * instead of a raw `Error: POST … failed: 402`.
 */
export const UNKNOWN_ENTITLEMENT_CODE = "premium_required";

export type EntitlementCodeOrUnknown = EntitlementCode | typeof UNKNOWN_ENTITLEMENT_CODE;

/** Narrow an unknown value (a parsed problem+json field) to a known code. */
export function isEntitlementCode(value: unknown): value is EntitlementCode {
  return typeof value === "string" && (ENTITLEMENT_CODES as readonly string[]).includes(value);
}

/**
 * The parsed facts of a 402 — everything the UI needs to speak about it, and
 * nothing more. Structural on purpose: `EntitlementRequiredError` (api.ts)
 * satisfies it, and a test or a future caller can pass a plain object without
 * importing the API client.
 */
export interface EntitlementDenial {
  code: EntitlementCodeOrUnknown;
  /** The threshold that was hit (numeric limits only, e.g. 15). */
  limit?: number;
  /** The cookbook's current usage against that threshold (numeric limits only). */
  current?: number;
}

/**
 * The message for the three PAID IMPORT capabilities (photo / PDF / video).
 *
 * The first sentence is the product owner's own wording, chosen after seeing
 * the flow in prod (issue #19). The second sentence stays because without it
 * the user concludes that ALL import is closed — website import is free on
 * every plan (the API never answers 402 for it), so this is a way forward that
 * actually works today, not a consolation.
 *
 * Deliberately NOT used by `sharing` or `recipe_limit`: neither is about
 * import, so "du kan fortsatt importere fra en nettsidelenke" would be a
 * non-sequitur there, and `recipe_limit` must stay owner-neutral and numeric
 * (see its own arm below).
 */
const PAID_IMPORT_MESSAGE =
  "Denne funksjonaliteten er kun tilgjengelig i Premium abonnement. " +
  "Du kan fortsatt importere fra en nettsidelenke.";

/**
 * The user-facing Norwegian message for a denial.
 *
 * Tone: name what is closed and what the way forward is. Never blame the user
 * for hitting a limit, never imply data was lost (the recipe limit is a SOFT
 * limit server-side — everything already stored stays readable and editable),
 * and never mention anyone else's billing situation (the API deliberately
 * doesn't tell us whose plan governs the cookbook).
 */
export function entitlementMessage(denial: EntitlementDenial): string {
  switch (denial.code) {
    case "recipe_limit": {
      // The numbers come from the server (`current`/`limit`) — never computed
      // here, so the message can't contradict what the API enforced.
      //
      // OWNER-NEUTRAL WORDING, deliberately: §2.1's locked decision is that the
      // OWNER's plan governs the cookbook, so the person who hits this limit is
      // often a `write` member in someone else's cookbook who can neither
      // upgrade it nor is "the one with 15 recipes". Never "du har …" — say what
      // is true of the COOKBOOK, exactly like the server's own prose does.
      const counts =
        typeof denial.current === "number" && typeof denial.limit === "number"
          ? `Kokeboken har ${denial.current} av ${denial.limit} oppskrifter på Gratis. `
          : "Kokeboken er full på Gratis. ";
      return (
        `${counts}Premium gir plass til flere. ` +
        "Alt du har lagret blir liggende, og kan leses og endres som før."
      );
    }
    case "sharing":
      return (
        "Deling av kokebok er med i Premium. " +
        "På Gratis er kokeboken din alene; med Premium kan du invitere andre inn i den."
      );
    // The three paid imports deliberately share ONE message (one product
    // fact: "this capability is Premium"), but keep THREE separate arms rather
    // than a fall-through group, so any one of them can be re-worded later by
    // editing its own arm — no structural change, and no risk of silently
    // re-wording the other two. The API still spends a distinct CODE per
    // capability; that stays true regardless of the copy.
    case "photo_import":
      return PAID_IMPORT_MESSAGE;
    case "pdf_import":
      return PAID_IMPORT_MESSAGE;
    case "video_import":
      return PAID_IMPORT_MESSAGE;
    default:
      return "Dette er med i Premium. Resten av kokeboken fungerer som før.";
  }
}

/**
 * Is this value a denial we can render as a `PremiumNotice`?
 *
 * Needed because a denial now reaches the UI from TWO directions, and the
 * components that render it keep BOTH in one state field so they stay mutually
 * exclusive with the ordinary inline error string:
 *  1. the server's 402, parsed into an `EntitlementRequiredError` (api.ts), and
 *  2. the client's own UP-FRONT gate — a paid capability the active cookbook
 *     doesn't have (`CookbookSummary.isPremium === false`), refused before any
 *     work is asked of the user (issue #19). No request is made, so there is no
 *     Error to carry it: a plain `{ code: "photo_import" }` is the honest shape.
 * An `instanceof EntitlementRequiredError` check cannot see (2), hence the
 * structural test. The up-front gate is UX ONLY — never a security boundary;
 * the server enforces every limit and the 402 path above stays as the backstop
 * for the real race (the plan can change after the page loaded).
 */
export function isEntitlementDenial(value: unknown): value is EntitlementDenial {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { code?: unknown }).code === "string"
  );
}
